/**
 * KPI Count-Up — bis zu vier große Kennzahlen, die von 0 hochzählen.
 *
 * Allround-Daten-Szene für „harte Zahlen": Händler, Städte, Bewertungen,
 * Provision … Jede Kennzahl zählt per GSAP-`onUpdate` deterministisch hoch
 * (Hyperframes seekt die Timeline frameweise, daher rendert der Zähler exakt),
 * mit Akzent-Unterstrich, der mitwächst, und einem Aufblitzen beim Zielwert.
 *
 * Items-Format:  "Wert | Label | Suffix"
 *   z. B. "16 | Händler | +"   "4.9 | Bewertung | ★"   "0 | Risiko | %"
 *   - Wert: Zahl (Ganzzahl oder mit einer Nachkommastelle).
 *   - Suffix optional (wird an den Zielwert gehängt, z. B. %, +, ★, k).
 *   - Prefix optional über führendes Zeichen im Wert (z. B. "€12" → Prefix €).
 *
 * extras.eyebrow / inputs.text → optionaler Header über den Zahlen.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { splitItem } from "../visual/_mockHelpers.js";

const COUNT_START = 6 / 30;
const COUNT_DUR = 1.5;

export const kpiCountUp: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent];
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const headline = inputs.text ?? "";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "16 | Händler | +",
        "11 | Städte | ",
        "7 | Kategorien | ",
        "0 | Risiko | %",
      ]
  ).slice(0, isVertical ? 4 : 4);

  const mega = typeSize("mega", ctx.format);
  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const parsed = items.map((raw, i) => {
    const [valRaw, label, suffixRaw] = splitItem(raw);
    const v = (valRaw ?? "").trim();
    const prefixMatch = v.match(/^[^\d.\-]+/);
    const prefix = prefixMatch ? prefixMatch[0] : "";
    const target = parseFloat(v.replace(/[^0-9.\-]/g, "")) || 0;
    return {
      prefix,
      target,
      isInt: Number.isInteger(target),
      suffix: (suffixRaw ?? "").trim(),
      label: (label ?? "").trim(),
      color: palette[i % palette.length] ?? accent,
    };
  });

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const eyeId = `${clipPrefix}-eye`;
  const rowId = `${clipPrefix}-row`;

  const numIds = parsed.map((_, i) => `${clipPrefix}-num-${i}`);
  const lineIds = parsed.map((_, i) => `${clipPrefix}-ln-${i}`);
  const labelIds = parsed.map((_, i) => `${clipPrefix}-lb-${i}`);
  const cellIds = parsed.map((_, i) => `${clipPrefix}-cell-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const numPx = isVertical ? mega * 0.72 : mega * 0.78;
  const labelPx = isVertical ? sm * 0.72 : lg * 0.4;
  const cols = isVertical ? 2 : parsed.length;

  function renderCell(i: number, it: (typeof parsed)[number]): string {
    return `<div id="${cellIds[i]}" style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 16}px;will-change:transform,opacity;">
      <div id="${numIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${numPx}px;line-height:0.92;letter-spacing:-3px;color:${themeColors.fg};font-variant-numeric:tabular-nums;text-shadow:0 0 60px ${withAlpha(it.color, 0.5)};">${escapeHtml(it.prefix)}0${escapeHtml(it.suffix)}</div>
      <div id="${lineIds[i]}" style="height:4px;width:${isVertical ? 64 : 88}px;border-radius:9999px;background:linear-gradient(90deg, ${it.color}, ${withAlpha(it.color, 0.2)});box-shadow:0 0 18px ${withAlpha(it.color, 0.6)};transform-origin:left center;"></div>
      <div id="${labelIds[i]}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${labelPx}px;letter-spacing:1px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;">${escapeHtml(it.label)}</div>
    </div>`;
  }

  const cellsHtml = parsed.map((it, i) => renderCell(i, it)).join("");
  const eyePx = sm * 0.62;
  const headPx = isVertical ? lg * 0.84 : lg * 0.78;
  const hasHeader = Boolean(headline || eyebrow);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 44 : 56}px;padding:${isVertical ? "84px 56px" : "96px 120px"};overflow:hidden;">
    ${hasHeader ? `<div style="display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;max-width:${isVertical ? 880 : 1200}px;">
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.6px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
    </div>` : ""}
    <div id="${rowId}" style="display:grid;grid-template-columns:repeat(${cols}, 1fr);gap:${isVertical ? "48px 24px" : "0 64px"};align-items:start;justify-items:center;width:100%;max-width:${isVertical ? 860 : 1400}px;">
      ${cellsHtml}
    </div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 20 }) : "",
    ...cellIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 30, scale: 0.85, transformOrigin: "50% 50%" })),
    ...lineIds.map((id) => gsapSetLine(`#${id}`, { scaleX: 0 })),
    ...labelIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 8 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.2));

  const cellStart = hasHeader ? 0.5 : 0.2;
  const cellStep = 0.16;

  parsed.forEach((it, i) => {
    const at = cellStart + i * cellStep;
    tweens.push(t.to(`#${cellIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.7)\"" }, at));
    const countAt = at + COUNT_START;
    const round = it.isInt ? "Math.round(v)" : "v.toFixed(1)";
    tweens.push(
      `tl.to({v:0},{v:${it.target},duration:${COUNT_DUR},ease:"power2.out",onUpdate:function(){var v=this.targets()[0].v;var el=document.getElementById(${JSON.stringify(numIds[i])});if(el)el.textContent=${JSON.stringify(it.prefix)}+(${round})+${JSON.stringify(it.suffix)};}},${(clipStartSec + countAt).toFixed(4)});`,
    );
    tweens.push(t.to(`#${lineIds[i]}`, { scaleX: 1, duration: COUNT_DUR, ease: "raw:\"power2.out\"" }, countAt));
    tweens.push(t.to(`#${labelIds[i]}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.2));
    // Aufblitzen beim Erreichen des Zielwerts.
    const popAt = countAt + COUNT_DUR;
    tweens.push(t.fromTo(`#${numIds[i]}`, { scale: 1 }, { scale: 1.08, duration: 0.16, ease: "raw:\"power2.out\"", yoyo: true, repeat: 1 }, popAt));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
